import { memo, useMemo } from "react"
import { format } from "date-fns"
import DOMPurify from "dompurify"
import {
	IconAtom2Outline18 as AtomIcon,
	IconArrowDottedRotateAnticlockwiseOutline18 as Completed,
	IconArrowTurnLeft2Outline18 as CornerUpLeft,
	IconCheckDoubleOutline18 as Done,
} from "nucleo-ui-outline-18"

import type { BrandfetchData } from "@productlane/api/src/browser"
import type { FeedbackOrigin } from "@productlane/db/browser"
import type {
	FeedbackWithRelations,
	TeamMember,
} from "@productlane/zero/queries"
import {
	cn,
	convertHtmlToText,
	removeAllBrTags,
} from "@productlane/lib/src/browser"
import { formatRelativeTime } from "@productlane/lib/src/date"
import { FeedbackOriginPhraseRecord } from "@productlane/lib/src/feedback-origin"
import { Avatar, Checkbox, ImportantIcon, Tooltip } from "@productlane/ui"
import { TeamsIcon } from "@productlane/ui/src/icons/project/ms-teams-icon"

import { MemberHoverCard } from "@/components/member-hover-card"
import { ThreadLockIndicator } from "@/components/thread-list/thread-lock-indicator"
import { getContactDisplayName } from "@/lib/email-utils"
import { usePortalMarkById } from "@/lib/use-portal-marks"
import { useReadTransition } from "@/lib/use-read-transition"
import { UserCompanyAvatar } from "@/screens/app/customers/user-company-avatar"
import {
	useLastThreadMessage,
	useLastThreadMessageAuthorFirstName,
	useLastThreadMessageIsTeamReply,
} from "@/screens/app/suggestion/use-thread-message"
import { isEmptyContentTag } from "@/screens/app/threads/helpers"
import { useThreadsMultiSelectState } from "@/screens/app/threads/multi-select/threads-multi-select-state"
import { RoleCheck } from "../shared/role-check"
import { PortalCornerMark, showsPortalCornerMark } from "./portal-corner-mark"
import { ResponseBadges } from "./response-badges"
import { UnreadDot } from "./unread-dot"
import { useResponseBadge } from "./use-response-badge"

function ThreadListOriginIcon({ origin }: { origin: FeedbackOrigin }) {
	const icon =
		origin === "AI_CHAT" ? (
			<AtomIcon strokeWidth={1.5} className="h-3 w-3 flex-none text-tertiary" />
		) : origin === "TEAMS" ? (
			<TeamsIcon className="h-3 w-3 flex-none" />
		) : null

	if (!icon) return null

	return <Tooltip label={FeedbackOriginPhraseRecord[origin]}>{icon}</Tooltip>
}

interface ThreadListItemProps {
	thread: FeedbackWithRelations
	withResponseBadge?: boolean
	teamMembers: readonly TeamMember[]
	compact?: boolean
	rounded?: boolean
	isActive?: boolean
	searchHit?: { formattedText?: string | null }
	showAssignee?: boolean
	hideResolutionState?: boolean
}

// Tags allowed in the rendered preview. Kept out at module scope so the
// memoized sanitize call below has stable inputs.
const ALLOWED_PREVIEW_TAGS = ["span", "mark", "em", "strong"] as const

function useLastMessageHtml(thread: FeedbackWithRelations) {
	const lastMessageText = useLastThreadMessage(thread, "desc")

	return useMemo(
		() =>
			DOMPurify.sanitize(
				removeAllBrTags(
					convertHtmlToText(lastMessageText) ||
						convertHtmlToText(
							!isEmptyContentTag(thread?.text) ? (thread?.text ?? "") : "",
						),
				),
				{ ALLOWED_TAGS: [...ALLOWED_PREVIEW_TAGS] },
			),
		[thread.text, lastMessageText],
	)
}

export const ThreadListItem = memo(function ThreadListItem({
	thread,
	withResponseBadge,
	compact = false,
	isActive = false,
	searchHit,
	showAssignee = false,
	hideResolutionState = false,
}: ThreadListItemProps) {
	const ResponseBadge = useResponseBadge(thread)
	const convertedLastMessage = useLastMessageHtml(thread)
	const lastMessageAuthorFirstName = useLastThreadMessageAuthorFirstName(
		thread,
		"desc",
	)
	const lastMessageIsTeamReply = useLastThreadMessageIsTeamReply(thread, "desc")
	const { threadIds, toggleThreadId } = useThreadsMultiSelectState()
	const read = useReadTransition(
		!thread?.isRead,
		"-translate-x-3.5",
		thread?.id,
	)

	const completedItem = useMemo(() => {
		if (hideResolutionState) return null
		if (thread?.state !== "COMPLETED") return null
		const allItems = [
			...(thread.issues ?? []).map((i) => ({
				title: i.title,
				completedAt: i.completedAt ?? 0,
			})),
			...(thread.projects ?? []).map((p) => ({
				title: p.name,
				completedAt: p.completedAt ?? 0,
			})),
		]
		if (allItems.length === 0) return null
		const completedItems = allItems.filter((item) => item.completedAt > 0)
		if (completedItems.length > 0) {
			return completedItems.reduce((mostRecent, current) =>
				current.completedAt > mostRecent.completedAt ? current : mostRecent,
			)
		}
		return allItems[0] ?? null
	}, [hideResolutionState, thread?.state, thread?.issues, thread?.projects])

	const completedItemTitle = completedItem?.title ?? null

	const isChecked = useMemo(() => {
		return threadIds.includes(thread?.id ?? "")
	}, [thread?.id, threadIds])

	const waitingSince = useMemo(() => {
		const since = thread?.lastInboundMessageAt ?? thread?.createdAt
		return since ? new Date(since) : null
	}, [thread?.lastInboundMessageAt, thread?.createdAt])

	const portalMarks = usePortalMarkById()
	const portalMark = thread?.portalInstanceId
		? (portalMarks.get(thread.portalInstanceId) ?? null)
		: null

	if (!thread) {
		return null
	}

	const showResolutionState = !hideResolutionState
	const showResponseBadge =
		withResponseBadge && !(showResolutionState && thread.state === "COMPLETED")

	return (
		<div
			className={cn(
				"group relative box-content flex select-none items-center overflow-visible rounded-md pb-0.5 pr-2.5 pt-0.5 text-sm",
				{
					"sm:py-1": !compact,
				},
			)}
		>
			<div
				className={cn(
					"flex w-1/5 min-w-40 flex-1 items-center overflow-visible",
					{
						"sm:flex-none": !compact,
					},
				)}
			>
				<div
					className={cn(
						"group/avatar relative ml-[2px] flex h-11 w-8 flex-none items-center justify-center",
						{ "sm:ml-3": !compact },
					)}
				>
					<RoleCheck not={["VIEWER"]}>
						<div
							className={cn(
								// Same lift as the avatar it covers, so hover swaps in place.
								"absolute inset-0 z-10 hidden -translate-y-[8px] items-center justify-center group-hover/avatar:flex",
								{
									flex: isChecked,
								},
							)}
						>
							<Checkbox
								checked={isChecked}
								size="sm"
								onClick={(e: React.MouseEvent) => {
									e.preventDefault()
									e.stopPropagation()
									toggleThreadId(thread.id)
								}}
							/>
						</div>
					</RoleCheck>
					<div
						className={cn(
							"relative flex h-5 w-5 -translate-y-[8px] items-center justify-center",
							{
								"group-hover/avatar:invisible": !isChecked,
								invisible: isChecked,
							},
						)}
					>
						{portalMark &&
							showsPortalCornerMark({
								showResolutionState,
								state: thread.state,
							}) && <PortalCornerMark mark={portalMark} />}
						{showResolutionState && thread.state === "COMPLETED" ? (
							<div className="flex h-5 w-5 items-center justify-center rounded bg-gray-150 text-green-600 dark:bg-gray-800 dark:text-green-500">
								<Completed strokeWidth={2} className="h-3.5 w-3.5" />
							</div>
						) : showResolutionState && thread.state === "PROCESSED" ? (
							<div className="flex h-5 w-5 items-center justify-center rounded bg-gray-150 text-green-500 dark:bg-gray-800">
								<Done strokeWidth={2} className="h-3.5 w-3.5" />
							</div>
						) : lastMessageIsTeamReply ? (
							<div className="flex h-5 w-5 items-center justify-center rounded bg-gray-150 text-secondary dark:bg-gray-800">
								<CornerUpLeft strokeWidth={2} className="h-3 w-3" />
							</div>
						) : (
							<UserCompanyAvatar
								thread={thread}
								size="sm"
								brandfetchData={
									thread?.company?.companyMeta
										?.brandfetchData as unknown as BrandfetchData
								}
								name={thread?.contact?.name || thread?.contact?.email}
								colorSeed={thread?.contact?.email}
								image={thread?.contact?.imageUrl}
								linearLogoUrl={thread?.company?.logoUrl || undefined}
								painLevel={compact ? undefined : thread.painLevel}
							/>
						)}
					</div>
				</div>

				<div
					className={cn(
						"ml-[6px] mr-0 flex min-w-28 flex-1 flex-col items-start gap-0.5 overflow-visible truncate pt-1",
						{
							"sm:mr-3 sm:h-11 sm:flex-none sm:justify-center": !compact,
						},
					)}
				>
					{compact ? (
						<div
							className={cn(
								"flex w-full items-center overflow-visible text-sm font-medium",
								{
									"text-primary": !thread.isRead || isActive,
									"text-tertiary": thread.isRead && !isActive,
								},
							)}
						>
							<UnreadDot show={read.showDot} className={read.dotClassName} />

							{thread?.contact ? (
								<span
									className={cn(
										"truncate opacity-80 dark:opacity-100",
										read.slideClassName,
									)}
								>
									<span className="capitalize">
										{thread?.company
											? getContactDisplayName(thread.contact)?.split(" ")[0]
											: getContactDisplayName(thread.contact)}
									</span>
									{thread?.company && (
										<span>
											<span className="relative -top-px mx-1 text-[0.55rem] leading-none text-tertiary">
												•
											</span>
											{thread.company.name}
										</span>
									)}
								</span>
							) : thread?.company ? (
								<span
									className={cn(
										"truncate opacity-80 dark:opacity-100",
										read.slideClassName,
									)}
								>
									{thread.company.name}
								</span>
							) : (
								<span
									className={cn(
										"tracking-tight opacity-80 dark:opacity-100",
										read.slideClassName,
									)}
								>
									No customer
								</span>
							)}

							{/* Assignee last, so it stays flush right in the timestamp's column. */}
							<div className="ml-auto flex flex-none -translate-y-[2px] items-center gap-1.5">
								<ThreadLockIndicator threadId={thread.id} />
								{showResponseBadge && ResponseBadge?.extraBadges}
								{showResponseBadge && ResponseBadge?.showWaitingClock && (
									<ResponseBadges.WaitingClockIcon
										waitingSince={waitingSince}
									/>
								)}
								{thread.painLevel === "HIGH" && (
									<Tooltip label="Marked as important">
										<ImportantIcon className="mx-0.5 h-2.5 w-2.5 flex-none text-red-500 dark:text-red-400" />
									</Tooltip>
								)}
								{showAssignee && thread?.assignee && (
									<Tooltip
										label={thread.assignee.name ?? thread.assignee.email}
										shouldWrapChildren
									>
										<Avatar
											image={thread.assignee.imageUrl}
											name={thread.assignee.name ?? thread.assignee.email ?? ""}
											size="xs"
											roundness="full"
											className="h-[15px] min-h-[15px] w-[15px] min-w-[15px]"
										/>
									</Tooltip>
								)}
							</div>
						</div>
					) : (
						<div
							className={cn("flex flex-col items-start gap-0", {
								"sm:flex-row sm:items-center sm:gap-1.5": !compact,
							})}
						>
							{!!thread?.company && (
								<div
									className={cn(
										"flex h-[1.2rem] max-w-28 text-base font-medium",
										{
											"sm:text-sm": !compact,
										},
									)}
								>
									<span
										className={cn("truncate", {
											"font-medium text-primary": !!thread?.contact,
											"font-medium": !thread?.contact,
										})}
									>
										{thread?.company.name}
									</span>
								</div>
							)}
							{thread?.contact ? (
								<div
									className={cn(
										"h-[1.2rem] max-w-28 truncate capitalize tracking-tight",
										{
											"hidden sm:block": !compact,
											"text-tertiary": !!thread?.company,
											"font-medium text-primary": !thread?.company,
										},
									)}
								>
									{getContactDisplayName(thread.contact)}
								</div>
							) : !thread?.company ? (
								<div className="font-medium tracking-tight text-primary">
									No customer
								</div>
							) : null}
						</div>
					)}

					{compact && (
						<div className="flex w-full items-center gap-2 pb-1">
							<div className="flex min-w-0 flex-1 items-center gap-1">
								<ThreadListOriginIcon origin={thread.origin} />
								<div className="min-w-0 flex-1 truncate opacity-80 dark:opacity-100">
									{searchHit?.formattedText ? (
										<div
											className={cn(
												"truncate text-xs font-[480] tracking-tighter",
												!thread.isRead || isActive
													? "text-secondary"
													: "text-tertiary",
											)}
											dangerouslySetInnerHTML={{
												__html: DOMPurify.sanitize(searchHit.formattedText, {
													ALLOWED_TAGS: ["span", "mark", "em", "strong"],
												}),
											}}
										/>
									) : completedItemTitle ? (
										<div
											className={cn(
												"truncate text-xs font-[480] tracking-tighter",
												!thread.isRead || isActive
													? "text-secondary"
													: "text-tertiary",
											)}
										>
											<span>Completed: </span>
											{completedItemTitle}
										</div>
									) : thread.title && !convertedLastMessage ? (
										<div
											className={cn(
												"truncate text-xs font-[480] tracking-tighter",
												!thread.isRead || isActive
													? "text-secondary"
													: "text-tertiary",
											)}
										>
											{thread.title}
										</div>
									) : !convertedLastMessage ? (
										<div
											className={cn(
												"truncate text-xs font-[480] tracking-tighter",
												!thread.isRead || isActive
													? "text-secondary"
													: "text-tertiary",
											)}
										>
											Empty thread
										</div>
									) : (
										<div
											className={cn(
												"truncate text-xs font-[480] tracking-tighter",
												!thread.isRead || isActive
													? "text-secondary"
													: "text-tertiary",
											)}
										>
											{lastMessageAuthorFirstName && (
												<span className="capitalize">
													{lastMessageAuthorFirstName}:{" "}
												</span>
											)}
											<span
												dangerouslySetInnerHTML={{
													__html: convertedLastMessage,
												}}
											/>
										</div>
									)}
								</div>
							</div>
							{thread.state === "SNOOZED" && thread.snoozedUntil ? (
								<ResponseBadges.UnsnoozedAt
									snoozedUntilIso={new Date(thread.snoozedUntil).toISOString()}
								/>
							) : (
								<Tooltip
									label={format(
										new Date(thread.lastMessageAt),
										"EEE, MMM d 'at' h:mm a",
									)}
								>
									<span
										className={cn(
											"flex-none text-xs opacity-80 dark:opacity-100",
											!thread.isRead || isActive
												? "text-secondary"
												: "text-tertiary",
										)}
									>
										{formatRelativeTime(new Date(thread.lastMessageAt))}
									</span>
								</Tooltip>
							)}
						</div>
					)}
				</div>
			</div>

			{!compact && (
				<div className="hidden min-w-0 flex-1 flex-row items-center sm:flex">
					{showResponseBadge && (
						<div className="mr-2 flex items-center gap-2">
							{ResponseBadge?.extraBadges}
							{thread.painLevel === "HIGH" && (
								<ResponseBadges.Important className="ml-0 mr-0" />
							)}
							{ResponseBadge?.showWaitingClock && (
								<ResponseBadges.WaitingClock
									time={new Date(thread.lastMessageAt)}
								/>
							)}
						</div>
					)}
					{showResolutionState && thread.state === "COMPLETED" && (
						<div className="mr-2 flex items-center gap-2">
							{thread.painLevel === "HIGH" && (
								<ResponseBadges.Important className="ml-0 mr-0" />
							)}
							<ResponseBadges.CloseLoop
								issues={thread.issues}
								projects={thread.projects}
								className="ml-0 mr-0"
							/>
						</div>
					)}
					<UnreadDot show={read.showDot} className={read.dotClassName} />
					<div
						className={cn(
							"mr-3 min-w-0 max-w-[70%] flex-shrink truncate tracking-tight",
							{
								"font-medium text-primary": !thread.isRead,
								"text-tertiary": thread.isRead,
							},
							read.slideClassName,
						)}
					>
						{thread.title}
					</div>
					<div
						className={cn(
							"max-h-6 min-w-[20%] flex-1 truncate text-xs tracking-tight",
							!thread.isRead || isActive ? "text-secondary" : "text-tertiary",
							read.slideClassName,
						)}
						dangerouslySetInnerHTML={{
							__html: convertedLastMessage,
						}}
					/>
				</div>
			)}

			{!compact && (
				<div className="ml-4 hidden items-center gap-2 text-right text-xs sm:flex">
					{showResponseBadge && ResponseBadge ? (
						<ResponseBadges.Time
							time={new Date(thread.lastMessageAt)}
							isUnread={!thread.isRead || isActive}
						/>
					) : thread.state === "SNOOZED" ? (
						<ResponseBadges.UnsnoozedAt
							snoozedUntilIso={
								thread.snoozedUntil
									? new Date(thread.snoozedUntil).toISOString()
									: ""
							}
						/>
					) : (
						<ResponseBadges.CreatedAt
							time={new Date(thread.lastMessageAt)}
							isUnread={!thread.isRead || isActive}
						/>
					)}
				</div>
			)}

			{!compact && (
				<div className="ml-3 flex min-w-5 flex-none items-center justify-end">
					<MemberHoverCard
						userId={thread?.assigneeId}
						fallbackLabel="No assignee"
					>
						<Avatar
							showAvatarFallback={!thread?.assignee}
							{...(!thread?.assignee
								? {
										fallbackProps: {
											className:
												"text-gray-900 dark:text-gray-400 bg-gray-150 dark:bg-gray-800",
										},
									}
								: {})}
							image={thread?.assignee?.imageUrl}
							name={
								thread?.assignee?.name
									? thread?.assignee?.name
									: (thread?.assignee?.email ?? "No assignee")
							}
							size="xs"
							roundness="full"
						/>
					</MemberHoverCard>
				</div>
			)}
		</div>
	)
})
